import api from "./api";
import { parseISO } from "date-fns";
import { getExhibitorIcon } from "./StorageService";
import {
  CreateDistribution,
  CreatedDistribution,
  DistributionStatement,
  Exhibitor,
  Recipient,
  Response,
  Statement,
  VoucherType,
} from "../dtos";

const findAllDistributions = async (): Promise<Statement[]> => {
  const { data } = await api.get<Response<DistributionStatement[]>>(
    "distribution"
  );

  if (Object.keys(data).length === 0) return [];

  const statements = data.data.reduce((statements, distributionStatement) => {
    const {
      distributionId,
      distributionDate,
      exhibitorId,
      exhibitorUuid,
      exhibitorName,
      voucherTypeId,
      voucherTypeName,
      recipientId,
      recipientEmail,
      recipientName,
      recipientPhoneNumber,
      recipientAmount,
      recipientHash,
      redeemedDate,
      canceledDate,
      expiredDate,
      voucherBatchStatus,
    } = distributionStatement;

    const voucherType: Omit<VoucherType, "amount"> = {
      id: voucherTypeId,
      name: voucherTypeName,
    };

    const exhibitor: Exhibitor = {
      id: exhibitorId,
      name: exhibitorName,
      logo: getExhibitorIcon(exhibitorUuid),
      voucherTypes: [],
    };

    const recipient: Recipient = {
      id: recipientId,
      email: recipientEmail,
      name: recipientName,
      phoneNumber: recipientPhoneNumber,
      amount: recipientAmount,
      authorizationHash: recipientHash,
      redeemedDate: redeemedDate,
      canceledDate: canceledDate,
      expiredDate: expiredDate,
      voucherBatchStatus: voucherBatchStatus,
    };

    const index = statements.findIndex(
      (statement) => statement.id === distributionId
    );

    if (index !== -1) {
      const statement = statements[index];

      statements[index] = {
        ...statement,
        recipients: [...statement.recipients, recipient],
      };
    } else {
      statements.push({
        id: distributionId,
        date: parseISO(distributionDate),
        exhibitor,
        voucherType,
        recipients: [recipient],
      });
    }

    return statements;
  }, [] as Statement[]);

  return statements;
};

const createDistribution = async (
  dto: CreateDistribution
): Promise<CreatedDistribution> => {
  const { data } = await api.post<Response<CreatedDistribution>>(
    "distribution",
    dto
  );
  return data.data;
};

const createDistributionByBalance = async (
  dto: CreateDistribution
): Promise<CreatedDistribution> => {
  const { data } = await api.post<Response<CreatedDistribution>>(
    "distribution/balance",
    dto
  );
  return data.data;
};

const resendDistributionEmail = async (
  recipientsId: number[]
): Promise<void> => {
  await api.patch<void>("distribution/resend-email", {
    recipientsId,
  });
};

export {
  findAllDistributions,
  createDistribution,
  createDistributionByBalance,
  resendDistributionEmail,
};
